import { TaxonomyFacet } from '@edgebox/sync-core-data-definitions';
import { ISyncCoreApi, MonitoringPromptRunListFilters } from '@edgebox/sync-core-rest-client';
import React from 'react';
import { Link } from 'react-router';
import { BRAND_PRESENCE_AI_ANSWERS_PATH, BRAND_PRESENCE_CONTENT_INVENTORY_PATH } from '../brandPresencePaths';
import { ANALYZED_STATUSES } from '../GovernanceAnalyticsAiAnswers.utils';
import { GovernanceAnalyticsKpiCard } from './GovernanceAnalyticsKpiCard';
import { GovernanceAnalyticsKpiCardRow } from './GovernanceAnalyticsKpiCardRow';
import { associationListFilter, termPromptSections } from './termPromptAssociations.utils';

/**
 * The smallest page a list can answer with: the response carries the total, and no row is read.
 *
 * A number costs exactly this — one request, one row — so a screen showing three of them adds three
 * counts rather than three lists.
 *
 * One row rather than none because the shared database service rewrites a page size of zero to the
 * default one, which is ten to fifty rows fetched and thrown away for a number that sits in the
 * envelope. The constant drops to zero once that service answers zero with the count alone.
 */
export const COUNT_ONLY = { page: 0, itemsPerPage: 1 };

function total(response: { totalNumberOfItems?: number }): number {
  return response?.totalNumberOfItems ?? 0;
}

/** Which run-list filter selects the answers a tag of this facet is credited in, and its URL twin. */
interface IAnswersFilter {
  label: string;
  filter: 'citedTermKey' | 'aspectTermKey' | 'promptPurposeTermKey';
  param: 'citedTerm' | 'attribute' | 'purpose';
}

/**
 * The facets whose tags an answer can be selected BY, one at a time.
 *
 * Every other facet is credited on an answer by facet rather than by tag — the run list has no
 * per-tag filter for it — so a brand, a product or a rival gets no answers box at all. A number
 * whose list cannot reproduce it is worse than no number.
 */
const ANSWERS_FILTERS: Partial<Record<TaxonomyFacet, IAnswersFilter>> = {
  [TaxonomyFacet.AuthoritySource]: { label: 'Answers citing', filter: 'citedTermKey', param: 'citedTerm' },
  [TaxonomyFacet.OrganizationAttribute]: { label: 'Answers', filter: 'aspectTermKey', param: 'attribute' },
  [TaxonomyFacet.ProductAttribute]: { label: 'Answers', filter: 'aspectTermKey', param: 'attribute' },
  [TaxonomyFacet.MonitoringPromptPurpose]: { label: 'Answers', filter: 'promptPurposeTermKey', param: 'purpose' },
};

/** The run-list filter for one answers box, built by name so the client checks every field. */
function answersListFilter(answers: IAnswersFilter, key: string): MonitoringPromptRunListFilters {
  // Pinned to the statuses the list itself opens on, so the total and the rows behind it are one
  // set: counted over every status it would include the answers still being analysed and the failed
  // ones the list leaves out, and read as a list that lost rows on the way.
  const base: MonitoringPromptRunListFilters = { status: ANALYZED_STATUSES };

  switch (answers.filter) {
    case 'citedTermKey':
      return { ...base, citedTermKey: key };
    case 'aspectTermKey':
      return { ...base, aspectTermKey: key };
    default:
      return { ...base, promptPurposeTermKey: key };
  }
}

export interface ITermUsageBox {
  id: string;
  /** What the number counts, in the words the screen it links to uses. */
  label: string;
  /** Where the list this total came from opens on the same filter, where a screen takes it. */
  listPath?: string;
  /** The prompts behind this total are a face of this page rather than a screen of its own. */
  opensPrompts?: boolean;
  count: (api: ISyncCoreApi) => Promise<number>;
}

/**
 * How much of the project uses this tag, as one box per place it is used.
 *
 * A tag without a key gets no boxes: every list that counts it selects by key, so each box would
 * have nothing to ask for. Only a WriteAll caller sets a key, so a tag someone created here shows
 * its facts and no counts.
 */
export function termUsageBoxes(facet: TaxonomyFacet | undefined, termKey: string): ITermUsageBox[] {
  const key = termKey.trim();
  if (!key) {
    return [];
  }

  const boxes: ITermUsageBox[] = [
    {
      id: 'pages',
      label: 'Pages',
      listPath: `${BRAND_PRESENCE_CONTENT_INVENTORY_PATH}?${new URLSearchParams({ terms: key }).toString()}`,
      count: (api) => api.governance.contentItems.list(COUNT_ONLY, { taxonomyTerms: [key] }).then(total),
    },
  ];

  // The section the Prompts tab lists first for this facet — extraction targets where the facet is
  // measured, administrative where it classifies the prompt. The box is named after that section
  // rather than after the tab it opens: the tab lists every section the facet appears in, so a box
  // called "Prompts" would put a whole tab's name on one section's number. The facets no prompt
  // field holds get no box.
  const [section] = termPromptSections(facet);
  if (section) {
    boxes.push({
      id: 'prompts',
      label: section.label,
      opensPrompts: true,
      count: (api) => api.governance.monitoringPrompts.list(COUNT_ONLY, associationListFilter(section.type, key)).then(total),
    });
  }

  const answers = facet ? ANSWERS_FILTERS[facet] : undefined;
  if (answers) {
    boxes.push({
      id: 'answers',
      label: answers.label,
      listPath: `${BRAND_PRESENCE_AI_ANSWERS_PATH}?${new URLSearchParams({ [answers.param]: key }).toString()}`,
      count: (api) => api.governance.monitoringPromptRuns.list(COUNT_ONLY, answersListFilter(answers, key)).then(total),
    });
  }

  return boxes;
}

/** A total still on its way, a total that never arrived, or the number itself. */
export function countLabel(count: number | 'failed' | undefined): string {
  if (count === undefined) {
    return '…';
  }

  return count === 'failed' ? '—' : String(count);
}

/**
 * The same two states in words, for a reader who is listening rather than looking.
 *
 * "…" and "—" are marks a screen reader has nothing to say about, and "we could not count this" is
 * the one state a reader must not be left to infer from punctuation. A number reads as itself, so it
 * is left to the card.
 */
export function countStateLabel(count: number | 'failed' | undefined): string {
  if (count === undefined) {
    return 'Loading';
  }

  return count === 'failed' ? 'Unavailable' : '';
}

/**
 * What a tag is used by, across the project, above its own facts.
 *
 * Each box asks its own list for a total and links to that list filtered to this tag, so the number
 * is never a claim the reader has to take on trust — they click it and read the rows it counted. A
 * box whose request failed says so rather than reporting a zero that would read as "unused".
 */
export function GovernanceTermUsageCards({
  api,
  facet,
  termKey,
  onShowPrompts,
}: {
  api?: ISyncCoreApi;
  facet?: TaxonomyFacet;
  termKey: string;
  /** Opens the face of this page that lists the prompts themselves, where the page offers one. */
  onShowPrompts?: () => void;
}) {
  const boxes = React.useMemo(() => termUsageBoxes(facet, termKey), [facet, termKey]);
  const [counts, setCounts] = React.useState<Record<string, number | 'failed'>>({});
  /** The tag the totals in hand were counted for. */
  const [countedKey, setCountedKey] = React.useState(termKey);

  if (countedKey !== termKey) {
    // Dropped while rendering rather than from an effect, which runs after the commit: the boxes of
    // two tags carry the same three names, so the tag left behind would have its numbers painted
    // once under the new tag's — a total the reader has no reason to doubt and every reason to read.
    setCountedKey(termKey);
    setCounts({});
  }

  React.useEffect(() => {
    if (!api || !boxes.length) {
      return;
    }

    let active = true;

    for (const box of boxes) {
      void (async () => {
        try {
          const count = await box.count(api);
          if (active) {
            setCounts((current) => ({ ...current, [box.id]: count }));
          }
        } catch {
          // One list that cannot be counted is one box that says so; the others still report.
          if (active) {
            setCounts((current) => ({ ...current, [box.id]: 'failed' }));
          }
        }
      })();
    }

    return () => {
      active = false;
    };
  }, [api, boxes]);

  if (!api || !boxes.length) {
    return null;
  }

  return (
    <GovernanceAnalyticsKpiCardRow>
      {boxes.map((box) => {
        const card = (
          <>
            <GovernanceAnalyticsKpiCard
              title={box.label}
              value={countLabel(counts[box.id])}
              onClick={box.opensPrompts ? onShowPrompts : undefined}
            />
            {/* Spoken rather than drawn, and announced where the reader is when the total settles:
                the card itself changes one mark for another, which says nothing out loud. */}
            <span className="sr-only" aria-live="polite">
              {countStateLabel(counts[box.id])}
            </span>
          </>
        );

        return box.listPath ? (
          <Link key={box.id} to={box.listPath} className="block h-full no-underline">
            {card}
          </Link>
        ) : (
          <div key={box.id} className="h-full">
            {card}
          </div>
        );
      })}
    </GovernanceAnalyticsKpiCardRow>
  );
}
